import { WarningAmberOutlined } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { formatNurseName, formatPersonFullName } from "../../../utils/formatters";
import type { NurseInShift } from "../../../utils/models";
import { SynergyNurseInShiftMenu } from "../../SynergyModeStaffing/SynergyNurseInShiftMenu";

const CELL_HEIGHT = 120;

type Props = {
	date: DateTime;
	nurses: NurseInShift[];
	isOpen: boolean;
	credentialedNurseIds: number[];
	onCellClick: (date: DateTime) => void;
};

export const FacilityScheduleGridCell: FC<Props> = ({ date, nurses, isOpen, credentialedNurseIds, onCellClick }) => {
	const { getNurseById } = useHospitalData();
	const lastNameFirst = useLastNameFirst();
	const [menuState, setMenuState] = useState<{ nurse: NurseInShift; anchorEl: Element } | null>(null);

	return (
		<Box
			onClick={() => {
				if (menuState) {
					// avoid because it was triggered from the nurse actions menu
					return;
				}
				onCellClick(date);
			}}
			sx={{
				height: CELL_HEIGHT,
				border: isOpen ? "2px solid #7B68C8" : "1px solid rgb(236, 239, 241)",
				background: isOpen ? "#F0EDFB" : "transparent",
				padding: "4px",
				display: "flex",
				flexDirection: "column",
				gap: "2px",
				overflow: "hidden",
				cursor: "cell",
				"&:hover": {
					background: isOpen ? "#F0EDFB" : "#F8F7FF",
				},
			}}
		>
			<Typography
				sx={{
					fontSize: "11px",
					color: "#999",
					lineHeight: 1,
				}}
			>
				{date.toFormat("dd MMM")}
			</Typography>
			{nurses.map((nurseInShift) => {
				const nurseData = getNurseById(nurseInShift.nurseId);
				const fullName = nurseData ? formatPersonFullName(nurseData, lastNameFirst) : nurseInShift.name;
				const displayName = nurseData ? formatNurseName(nurseData, lastNameFirst) : nurseInShift.name;
				const isCredentialed = credentialedNurseIds.includes(nurseInShift.nurseId);

				return (
					<Box
						key={`shift-${nurseInShift.id}-${nurseInShift.nurseId}-${date.toISODate()}`}
						onClick={(e) => {
							e.stopPropagation();
							setMenuState({ nurse: nurseInShift, anchorEl: e.currentTarget });
						}}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.25,
							width: "fit-content",
							cursor: "pointer",
							"&:hover": { opacity: 0.7 },
							overflow: "hidden",
						}}
					>
						{!isCredentialed && (
							<InHouseTooltip title="Not credentialed for this facility">
								<WarningAmberOutlined sx={{ fontSize: "10px", color: "#E71C00CC", flexShrink: 0 }} />
							</InHouseTooltip>
						)}
						<Tooltip title={fullName}>
							<Typography
								sx={{
									fontSize: {
										xs: "12px",
										xl: "14px",
									},
									color: "primary.dark",
									lineHeight: 1.2,
									maxWidth: {
										xs: 140,
										xl: 180,
										xxl: 220,
									},
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{displayName}
							</Typography>
						</Tooltip>
					</Box>
				);
			})}
			{menuState && <SynergyNurseInShiftMenu nurseInShift={menuState.nurse} anchorEl={menuState.anchorEl} open={true} onClose={() => setMenuState(null)} />}
		</Box>
	);
};
